import * as React from "react"
import { Eraser, PenLine, Type } from "lucide-react"

import { cn } from "@/lib/utils"

/*
  SignaturePad — sign with a finger, pen or mouse; export a PNG (DN received on site, 21 #9 / 17 #8).

    const pad = useRef(null);
    <SignaturePad ref={pad} label="Received by" onChange={({ empty }) => setSigned(!empty)} />
    const blob = await pad.current.toBlob();               // PNG on white, or null when empty
    form.append('signature', blob, 'signature.png');

  - Strokes are kept as points, so resizing (rotating the phone) redraws the signature instead of losing it.
  - Keyboard / no pointer: "Type instead" draws the typed name in a handwriting style (allowTyped, default on).
  - The pad is always white with dark ink (like paper), also in the dark theme, so the PNG looks the same.
  Ref: toBlob(type = 'image/png') → Promise<Blob | null>, toDataURL() → string | null, clear(), isEmpty().
  Props: label (accessible name, default "Signature"), height (180), onChange({ empty, typed }), allowTyped,
  disabled, className, testId ('signature-pad').
*/
const INK = "#16163f"
const PAPER = "#ffffff"
const TYPED_FONT = "italic 600 34px 'Segoe Script', 'Brush Script MT', 'Lucida Handwriting', cursive"

function drawStrokes(ctx, strokes, width, height) {
  ctx.lineCap = "round"
  ctx.lineJoin = "round"
  ctx.strokeStyle = INK
  ctx.fillStyle = INK
  ctx.lineWidth = 2.4
  strokes.forEach((stroke) => {
    if (!stroke.length) return
    const points = stroke.map(([x, y]) => [x * width, y * height])
    if (points.length === 1) {
      ctx.beginPath()
      ctx.arc(points[0][0], points[0][1], 1.4, 0, Math.PI * 2)
      ctx.fill()
      return
    }
    ctx.beginPath()
    ctx.moveTo(points[0][0], points[0][1])
    for (let i = 1; i < points.length - 1; i += 1) {
      const midX = (points[i][0] + points[i + 1][0]) / 2
      const midY = (points[i][1] + points[i + 1][1]) / 2
      ctx.quadraticCurveTo(points[i][0], points[i][1], midX, midY)
    }
    const last = points[points.length - 1]
    ctx.lineTo(last[0], last[1])
    ctx.stroke()
  })
}

function drawTyped(ctx, text, width, height) {
  if (!text) return
  ctx.fillStyle = INK
  ctx.textAlign = "center"
  ctx.textBaseline = "middle"
  ctx.font = TYPED_FONT
  ctx.fillText(text, width / 2, height / 2, width - 32)
}

const SignaturePad = React.forwardRef(({
  label = "Signature",
  height = 180,
  onChange,
  allowTyped = true,
  disabled = false,
  className,
  testId = "signature-pad",
}, ref) => {
  const canvasRef = React.useRef(null)
  const boxRef = React.useRef(null)
  const strokesRef = React.useRef([])
  const drawingRef = React.useRef(false)
  const sizeRef = React.useRef({ width: 0, height })
  const [mode, setMode] = React.useState("draw") // draw | type
  const [typed, setTyped] = React.useState("")
  const [empty, setEmpty] = React.useState(true)
  const inputId = React.useId()

  const render = React.useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext && canvas.getContext("2d")
    if (!ctx) return
    const { width, height: h } = sizeRef.current
    const ratio = window.devicePixelRatio || 1
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0)
    ctx.clearRect(0, 0, width, h)
    if (mode === "type") drawTyped(ctx, typed.trim(), width, h)
    else drawStrokes(ctx, strokesRef.current, width, h)
  }, [mode, typed])

  const resize = React.useCallback(() => {
    const canvas = canvasRef.current
    const box = boxRef.current
    if (!canvas || !box) return
    const width = box.clientWidth || 320
    const ratio = window.devicePixelRatio || 1
    sizeRef.current = { width, height }
    canvas.width = Math.round(width * ratio)
    canvas.height = Math.round(height * ratio)
    canvas.style.width = `${width}px`
    canvas.style.height = `${height}px`
    render()
  }, [height, render])

  React.useEffect(() => {
    resize()
    if (typeof ResizeObserver === "undefined" || !boxRef.current) return undefined
    const observer = new ResizeObserver(() => resize())
    observer.observe(boxRef.current)
    return () => observer.disconnect()
  }, [resize])

  const update = React.useCallback((nextEmpty) => {
    setEmpty(nextEmpty)
    onChange?.({ empty: nextEmpty, typed: mode === "type" })
  }, [onChange, mode])

  const point = (event) => {
    const rect = canvasRef.current.getBoundingClientRect()
    const { width, height: h } = sizeRef.current
    const x = (event.clientX - rect.left) / (rect.width || width || 1)
    const y = (event.clientY - rect.top) / (rect.height || h || 1)
    return [Math.max(0, Math.min(1, x)), Math.max(0, Math.min(1, y))]
  }

  const onPointerDown = (event) => {
    if (disabled || mode !== "draw" || (event.button !== undefined && event.button > 0)) return
    event.preventDefault()
    drawingRef.current = true
    canvasRef.current.setPointerCapture?.(event.pointerId)
    strokesRef.current = [...strokesRef.current, [point(event)]]
    render()
  }
  const onPointerMove = (event) => {
    if (!drawingRef.current) return
    event.preventDefault()
    const strokes = strokesRef.current
    strokes[strokes.length - 1].push(point(event))
    render()
  }
  const onPointerUp = (event) => {
    if (!drawingRef.current) return
    drawingRef.current = false
    canvasRef.current.releasePointerCapture?.(event.pointerId)
    update(strokesRef.current.length === 0)
  }

  const clear = React.useCallback(() => {
    strokesRef.current = []
    setTyped("")
    update(true)
    window.requestAnimationFrame?.(() => render())
  }, [render, update])

  React.useEffect(() => {
    render()
  }, [render])

  const exportCanvas = React.useCallback(() => {
    if (empty) return null
    const { width, height: h } = sizeRef.current
    const ratio = window.devicePixelRatio || 1
    const out = document.createElement("canvas")
    out.width = Math.round(width * ratio)
    out.height = Math.round(h * ratio)
    const ctx = out.getContext && out.getContext("2d")
    if (!ctx) return null
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0)
    ctx.fillStyle = PAPER
    ctx.fillRect(0, 0, width, h)
    if (mode === "type") drawTyped(ctx, typed.trim(), width, h)
    else drawStrokes(ctx, strokesRef.current, width, h)
    return out
  }, [empty, mode, typed])

  React.useImperativeHandle(ref, () => ({
    clear,
    isEmpty: () => empty,
    toDataURL: (type = "image/png") => exportCanvas()?.toDataURL(type) || null,
    toBlob: (type = "image/png") => new Promise((resolve) => {
      const canvas = exportCanvas()
      if (!canvas || typeof canvas.toBlob !== "function") {
        resolve(null)
        return
      }
      canvas.toBlob((blob) => resolve(blob), type)
    }),
  }), [clear, empty, exportCanvas])

  const switchMode = (next) => {
    strokesRef.current = []
    setTyped("")
    setMode(next)
    setEmpty(true)
    onChange?.({ empty: true, typed: next === "type" })
  }

  return (
    <div className={cn("flex flex-col gap-2", className)} data-testid={testId}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[13px] font-semibold text-ink-2">{label}</span>
        <div className="flex items-center gap-1">
          {allowTyped ? (
            <button
              type="button"
              disabled={disabled}
              onClick={() => switchMode(mode === "draw" ? "type" : "draw")}
              className="inline-flex h-9 items-center gap-1.5 rounded-lg px-2.5 text-[12.5px] font-semibold text-brand-ink hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
              data-testid={`${testId}-mode`}
            >
              {mode === "draw" ? <Type className="h-4 w-4" aria-hidden="true" /> : <PenLine className="h-4 w-4" aria-hidden="true" />}
              {mode === "draw" ? "Type instead" : "Draw instead"}
            </button>
          ) : null}
          <button
            type="button"
            disabled={disabled || empty}
            onClick={clear}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg px-2.5 text-[12.5px] font-semibold text-ink-2 hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40"
            data-testid={`${testId}-clear`}
          >
            <Eraser className="h-4 w-4" aria-hidden="true" />Clear
          </button>
        </div>
      </div>
      <div
        ref={boxRef}
        className={cn(
          "relative overflow-hidden rounded-2xl border border-line bg-white shadow-card",
          disabled && "opacity-60"
        )}
        style={{ height }}
      >
        <canvas
          ref={canvasRef}
          role="img"
          aria-label={empty ? `${label}: empty — draw with a finger, pen or mouse${allowTyped ? ", or choose Type instead" : ""}` : `${label}: signed`}
          className={cn("block touch-none", mode === "draw" && !disabled ? "cursor-crosshair" : "")}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          data-testid={`${testId}-canvas`}
          data-empty={empty ? "true" : "false"}
        />
        {/* baseline + hint, like a paper form */}
        <span className="pointer-events-none absolute inset-x-6 bottom-9 border-b border-dashed border-[#c9cbe0]" aria-hidden="true" />
        {empty && mode === "draw" ? (
          <span className="pointer-events-none absolute bottom-3 left-6 text-[12px] font-medium text-[#686a8e]" aria-hidden="true">
            Sign above the line
          </span>
        ) : null}
      </div>
      {mode === "type" ? (
        <div className="flex flex-col gap-1">
          <label htmlFor={inputId} className="text-[12.5px] font-semibold text-ink-2">Type the full name</label>
          <input
            id={inputId}
            value={typed}
            disabled={disabled}
            onChange={(event) => {
              setTyped(event.target.value)
              const nextEmpty = !event.target.value.trim()
              setEmpty(nextEmpty)
              onChange?.({ empty: nextEmpty, typed: true })
            }}
            autoComplete="name"
            className="h-11 rounded-control border border-line bg-surface px-3 text-[15px] text-ink outline-none focus-visible:ring-2 focus-visible:ring-ring"
            data-testid={`${testId}-typed`}
          />
        </div>
      ) : null}
    </div>
  )
})
SignaturePad.displayName = "SignaturePad"

export { SignaturePad }
